import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  TrendingUp, Activity, Flame, NotebookPen, LineChart as LineIcon, Loader2, CalendarDays,
} from "lucide-react";
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Cell } from "recharts";
import AppLayout from "../components/AppLayout";
import { symptomLabel } from "../data";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Stat = ({ icon: Icon, label, value, tint }) => (
  <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid={`progress-stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>
    <div className="flex items-center gap-2 text-slate-500 text-[13px] font-medium"><Icon size={16} style={{ color: tint }} /> {label}</div>
    <p className="text-2xl font-head font-bold text-slate-800 mt-1">{value}</p>
  </div>
);

export default function MyProgress() {
  const [entries, setEntries] = useState(null);

  useEffect(() => {
    axios.get(`${API}/diary`).then((r) => setEntries(r.data.entries || [])).catch(() => setEntries([]));
  }, []);

  const data = useMemo(() => {
    if (!entries) return null;
    const sorted = [...entries].sort((a, b) => (a.date || "").localeCompare(b.date || ""));
    const severitySeries = sorted.map((e) => ({ date: (e.date || "").slice(5), severity: Number(e.severity) || 0 }));
    const episodes = sorted.filter((e) => e.flare).length;
    const sevVals = sorted.map((e) => Number(e.severity) || 0).filter(Boolean);
    const avg = sevVals.length ? (sevVals.reduce((a, b) => a + b, 0) / sevVals.length).toFixed(1) : "0";
    const freq = {};
    sorted.forEach((e) => (e.symptoms || []).forEach((s) => { freq[s] = (freq[s] || 0) + 1; }));
    const topSymptoms = Object.entries(freq).map(([id, count]) => ({ name: symptomLabel(id), count })).sort((a, b) => b.count - a.count).slice(0, 8);
    // recent vs earlier severity comparison
    const half = Math.floor(sevVals.length / 2);
    const earlier = half ? sevVals.slice(0, half).reduce((a, b) => a + b, 0) / half : 0;
    const recent = half ? sevVals.slice(half).reduce((a, b) => a + b, 0) / (sevVals.length - half) : 0;
    const direction = !half ? "flat" : recent < earlier - 0.3 ? "improving" : recent > earlier + 0.3 ? "worsening" : "stable";
    return { severitySeries, episodes, avg, topSymptoms, count: sorted.length, direction };
  }, [entries]);

  if (!data) return <AppLayout title="My Progress"><div className="p-12 text-center"><Loader2 className="animate-spin text-[#0d9488] mx-auto" /></div></AppLayout>;

  const trendCopy = {
    improving: { text: "Your average symptom severity has been trending down recently — keep it up.", color: "#22a35a", bg: "#e9f7ef" },
    worsening: { text: "Your average symptom severity has risen recently. Consider discussing this with your GP and reviewing possible triggers.", color: "#e0567a", bg: "#fdeceb" },
    stable: { text: "Your symptom severity has been broadly stable recently.", color: "#e08a2b", bg: "#fdf2e8" },
    flat: { text: "Log a few more diary entries to see your progress trend.", color: "#64748b", bg: "#f1f5f9" },
  }[data.direction];

  return (
    <AppLayout title="My Progress">
      <div className="bg-gradient-to-r from-[#0d9488] to-[#0f766e] rounded-2xl p-6 text-white flex items-center gap-4">
        <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center"><LineIcon size={26} /></div>
        <div><h2 className="font-head font-bold text-xl">Your health journey</h2><p className="text-white/85 text-sm mt-0.5">Track how your symptoms change over time from your diary.</p></div>
      </div>

      {data.count === 0 ? (
        <div className="bg-white rounded-2xl border border-slate-200 p-12 text-center mt-5" data-testid="progress-empty">
          <NotebookPen size={40} className="text-slate-300 mx-auto" />
          <p className="text-slate-500 mt-3">No diary entries yet. Log symptoms in your Symptom Diary to start tracking progress.</p>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-5">
            <Stat icon={NotebookPen} label="Entries" value={data.count} tint="#0d9488" />
            <Stat icon={Activity} label="Avg severity" value={`${data.avg}/5`} tint="#e08a2b" />
            <Stat icon={Flame} label="Episodes" value={data.episodes} tint="#e0567a" />
            <Stat icon={TrendingUp} label="Trend" value={data.direction === "flat" ? "—" : data.direction.charAt(0).toUpperCase() + data.direction.slice(1)} tint="#2b7fd9" />
          </div>

          <div className="rounded-xl p-4 mt-4 border" style={{ backgroundColor: trendCopy.bg, borderColor: trendCopy.color + "44" }} data-testid="progress-trend-note">
            <p className="text-sm font-medium" style={{ color: trendCopy.color }}>{trendCopy.text}</p>
          </div>

          <div className="bg-white rounded-2xl border border-slate-200 p-5 mt-5">
            <h3 className="font-head font-semibold text-slate-800 mb-2 flex items-center gap-2"><CalendarDays size={16} className="text-[#0d9488]" /> Symptom severity over time</h3>
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={data.severitySeries} margin={{ left: -20, right: 10, top: 6 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                <YAxis domain={[0, 5]} tick={{ fontSize: 11 }} />
                <Tooltip />
                <Line type="monotone" dataKey="severity" stroke="#0d9488" strokeWidth={2} dot={{ r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>

          <div className="bg-white rounded-2xl border border-slate-200 p-5 mt-5">
            <h3 className="font-head font-semibold text-slate-800 mb-2">Most frequent symptoms</h3>
            <ResponsiveContainer width="100%" height={Math.max(180, data.topSymptoms.length * 32)}>
              <BarChart data={data.topSymptoms} layout="vertical" margin={{ left: 10, right: 16 }}>
                <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11 }} />
                <YAxis type="category" dataKey="name" width={130} tick={{ fontSize: 11 }} />
                <Tooltip cursor={{ fill: "#f1f5f9" }} />
                <Bar dataKey="count" radius={[0, 4, 4, 0]}>{data.topSymptoms.map((e) => <Cell key={e.name} fill="#0d9488" />)}</Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </>
      )}
      <p className="text-[12px] text-slate-400 mt-6">This is a personal tracking view based on your diary, not a medical diagnosis. Share these trends with your clinician at appointments.</p>
    </AppLayout>
  );
}
